import { aptGenerationApi } from '@/api/apt_generation.api';
import { ActionButton } from '@/components/common/ActionButton';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { SkeletonBlock } from '@/components/common/Skeleton';
import { TOOL_MANAGEMENT_DIALOG_PT } from '@/components/dialogs/dialogTheme';
import { ArrowsIn, ArrowsOut } from '@phosphor-icons/react';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ToolpathDialogProps {
  visible: boolean;
  title: string;
  pocketProcedureId: string | null;
  onClose: () => void;
}

/**
 * Modal showing an interactive Plotly toolpath visualization inside an iframe.
 *
 * The iframe loads HTML produced server-side by `plot_toolpath` (one trace per
 * `.h` file).  We fetch the HTML through the authenticated apiClient as a
 * Blob, wrap it in `URL.createObjectURL`, and feed that to the iframe — a
 * plain `<iframe src="/api/v1/pocket-procedure/.../toolpath.html">` would 401
 * because iframes don't forward the parent app's Bearer token.
 */
export const ToolpathDialog: FC<ToolpathDialogProps> = ({ visible, title, pocketProcedureId, onClose }) => {
  const { t } = useTranslation();
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!visible || !pocketProcedureId) {
      return;
    }
    let cancelled = false;
    let activeUrl: string | null = null;
    setIsLoading(true);
    setError(null);
    setIframeUrl(null);
    aptGenerationApi
      .getPocketProcedureToolpathHtml(pocketProcedureId)
      .then((blob) => {
        if (cancelled) return;
        // Force text/html so the iframe renders the page instead of treating
        // it as a generic octet-stream when the blob type isn't set by axios.
        const htmlBlob = blob.type === 'text/html' ? blob : new Blob([blob], { type: 'text/html' });
        activeUrl = URL.createObjectURL(htmlBlob);
        setIframeUrl(activeUrl);
      })
      .catch(() => {
        if (!cancelled) setError(t('projectDetail.toolpathDialogError'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (activeUrl) URL.revokeObjectURL(activeUrl);
    };
  }, [visible, pocketProcedureId, t]);

  // Revoke the current object URL whenever the dialog closes — covers the
  // case where `visible` flips to false before the fetch resolves.
  useEffect(() => {
    if (!visible && iframeUrl) {
      URL.revokeObjectURL(iframeUrl);
      setIframeUrl(null);
    }
  }, [visible, iframeUrl]);

  const fullscreenLabel = isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen');

  const dialogHeader = (
    <div className="flex w-full min-w-0 items-center justify-between gap-4 pr-2">
      <span className="min-w-0 truncate text-lg font-semibold text-gray-900">{title}</span>
      <ActionButton
        variant="ghost"
        iconOnly={true}
        icon={isFullscreen ? <ArrowsIn size={16} aria-hidden="true" /> : <ArrowsOut size={16} aria-hidden="true" />}
        onClick={() => setIsFullscreen((prev) => !prev)}
        title={fullscreenLabel}
        aria-label={fullscreenLabel}
        className="shrink-0"
      />
    </div>
  );

  const dialogFooter = (
    <ActionButton variant="ghost" onClick={onClose}>
      {t('common.close')}
    </ActionButton>
  );

  return (
    <Dialog
      header={dialogHeader}
      footer={dialogFooter}
      visible={visible}
      onHide={onClose}
      modal={true}
      className={`w-full ${isFullscreen ? 'max-w-full h-full' : 'max-w-5xl'}`}
      dismissableMask={true}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        ...TOOL_MANAGEMENT_DIALOG_PT,
        root: {
          className: isFullscreen
            ? 'border border-gray-200 rounded-2xl shadow-lg fixed inset-0 h-full m-0'
            : TOOL_MANAGEMENT_DIALOG_PT.root.className,
        },
        content: {
          className: `${TOOL_MANAGEMENT_DIALOG_PT.content.className} ${isFullscreen ? 'h-full flex flex-col' : ''}`,
        },
      }}
    >
      <div className={`flex flex-col ${isFullscreen ? 'h-full' : ''}`}>
        {isLoading ? (
          <div className="rounded-xl bg-gray-50 p-4" aria-busy="true">
            <SkeletonBlock className={isFullscreen ? 'h-full min-h-[20rem] w-full' : 'h-[70vh] w-full'} />
            <span className="sr-only">{t('projectDetail.toolpathDialogLoading')}</span>
          </div>
        ) : error ? (
          <ErrorMessage message={error} />
        ) : iframeUrl ? (
          <iframe
            title={title}
            src={iframeUrl}
            // Plotly needs scripts; same-origin lets the modebar / hover work.
            sandbox="allow-scripts allow-same-origin"
            className={`w-full rounded-xl border-0 bg-gray-50 ${isFullscreen ? 'flex-1' : ''}`}
            style={isFullscreen ? undefined : { height: '70vh' }}
          />
        ) : null}
      </div>
    </Dialog>
  );
};
